import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Form, Formik } from 'formik';
import * as Yup from "yup";
import Breadcrumbs from '../../components/BreadCrumbs';
import StyledHeadingText from '../../components/Fonts/StyledHeadingText';
import { CustomCard } from '../../components/cards/CustomCard';
import Button from '../../components/buttons/Button';
import InputField from '../../components/inputFields/InputField';
import { Save } from 'lucide-react';
import AsyncFormikDropdown from '../../components/inputFields/AsyncFormikDropDown';
import { searchProductWithAkaName } from '../../services/search';
import { getKitDropdown } from '../../services/inventory';
import FormikDropdown from '../../components/inputFields/FormikDropDown';
import { toast } from 'react-toastify';

const ProductInventoryForm = () => {
    const { slug } = useParams();
    const navigate = useNavigate();
    const isEditMode = slug !== 'add';
    const [kitDropdown, setKitDropdown] = useState([]);

    const initialValues = {
        shop_product_inventory: '',
        quantity: '',
        threshold: '',
        location: '',
        kit: '',
        supplier: '',
    };

    // Mock data for edit mode
    useEffect(() => {
        fetchKitDropdown();
    }, []);

    const validationSchema = Yup.object({
        shop_product_inventory: Yup.string().required('Product is required'),
    });


    const fetchKitDropdown = async () => {
        try {
            let res = await getKitDropdown();
            if (res.status === 200) {
                const data = res.data;
                setKitDropdown(data);
            } else {
                toast.error("Unable to fetch Data");
            }
        } catch (error) {
            console.error("Error:", error);
        }
    };

    const handleSubmit = async (values, { setSubmitting }) => {
        try {
            // Simulate API call
            await new Promise(resolve => setTimeout(resolve, 1000));

            // Success - navigate back to products list
            navigate('/inventory/products');
        } catch (error) {
            console.error('Error saving product:', error);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            <Breadcrumbs items={[
                { name: 'Home', link: '/dashboard' },
                { name: 'Inventory' },
                { name: 'Products', link: '/inventory/products' },
                { name: isEditMode ? 'Edit Product' : 'Add Product' },
            ]} />

            <div className="flex justify-between items-center my-4">
                <StyledHeadingText
                    text={isEditMode ? "Edit Product" : "Add New Product"}
                    size={"text-2xl"}
                />
            </div>

            <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
            >
                {({ isSubmitting, values, setFieldValue }) => (
                    <Form>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            {/* Basic Information */}
                            <CustomCard title="Basic Information">
                                <div className="space-y-4">
                                    <div className="grid grid-cols-2 gap-4">
                                        <AsyncFormikDropdown
                                            label="Product"
                                            required
                                            name={`shop_product_inventory`}
                                            apiCallFunction={async (input) => {
                                                const response = await searchProductWithAkaName(input);
                                                return response.data.map((pro) => ({
                                                    value: pro.id,
                                                    label: pro.aka_name ?? pro.product_name,
                                                    price: pro.price,
                                                }));
                                            }}
                                            needSearch
                                            className="w-full"
                                            placeholder="Search product..."
                                        />
                                        <InputField
                                            label="Supplier"
                                            name="supplier"
                                            placeholder="Enter supplier"
                                        />
                                    </div>
                                </div>
                            </CustomCard>

                            {/* Stock Information */}
                            <CustomCard title="Stock Information">
                                <div className="space-y-4">
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        <InputField
                                            label="Location"
                                            name="location"
                                            placeholder="Enter storage location"
                                        />
                                        <InputField
                                            label="Current Stock"
                                            name="quantity"
                                            type="number"
                                            placeholder="Enter current stock"
                                        />
                                        <InputField
                                            label="Threshold"
                                            name="threshold"
                                            type="number"
                                            placeholder="Enter threshold"
                                        />
                                    </div>
                                </div>
                            </CustomCard>
                            <InputField
                                label="Note"
                                name="note"
                                placeholder="Enter note"
                            />
                        </div>

                        {/* Form Actions */}
                        <div className="mt-6 flex justify-end gap-4">
                            <Button
                                type="button"
                                onClick={() => navigate('/inventory/products')}
                                className="bg-secondary hover:bg-slate-600 text-white"
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                disabled={isSubmitting}
                                className="bg-primary hover:bg-green-500 flex items-center gap-2 text-white"
                            >
                                {isSubmitting ? (
                                    <>
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                        Saving...
                                    </>
                                ) : (
                                    <>
                                        <Save className="w-4 h-4" />
                                        {isEditMode ? 'Update Product' : 'Save Product'}
                                    </>
                                )}
                            </Button>
                        </div>
                    </Form>
                )}
            </Formik>
        </>
    );
};

export default ProductInventoryForm; 